/**
 * @fileoverview What an order line IS (`order_line_items."type"`) — its vocabulary, declared once
 * (REGISTER-WEB-ORDERS-001 A4; owner ruling Q-RWO-1, 2026-10-23, "Register table + *Amount").
 *
 * ## Why it lives here
 *
 * The register's line table is now the ONE line table: a ParcelPuffin register sale and a PurchasePuffin web order
 * both write their lines there, and the register, the storefront, the admin and the console all read them. The type
 * was declared in pos-api's `constants/orderLineTypes.js` and read by eight pos modules; every reader now takes it from
 * here.
 *
 * ## The types
 *
 * - `product` — goods or a service sold on the order (every storefront line is one).
 * - `shipping` — the shipment a register sale books (owner ruling Q-RSE-23): no product; its `metadata.shipping` holds
 *   the chosen rate, which the ledger leg books from.
 * - `gift_card` — a gift card SOLD at the register (COMPLIANCE-REPORTING-001 F-CR-3 unit 4, owner rulings Q-CR-15 /
 *   Q-CR-16): no product, priced at the amount the cashier typed, never discounted and never taxed (it sells stored
 *   value, not goods). The sale that pays the order issues the card.
 *
 * `order_line_items.type` is a `varchar(50)` with no CHECK (measured on the live catalog capture of 2026-10-20), so a
 * new member needs no migration.
 *
 * ORD1's order status vocabulary (REGISTER-WEB-ORDERS-001 B1) and `orderSource` are this module's neighbours.
 *
 * @module @puffin-core/status-taxonomy/orderLineType
 */

'use strict';

/**
 * What an order line is.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_LINE_TYPE = Object.freeze({
  PRODUCT: 'product',
  SHIPPING: 'shipping',
  GIFT_CARD: 'gift_card',
});

/**
 * Every line type, in declaration order — what a validator admits and a CHECK names.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_LINE_TYPE_VALUES = Object.freeze(Object.values(ORDER_LINE_TYPE));

/**
 * The line kinds the register never discounts (`discountable: false` in the pricer): their order-discount weight is 0,
 * so a return allocates the order's discount across the goods alone, exactly as the sale took it
 * (COMPLIANCE-REPORTING-001 F-CR-3 unit 4).
 *
 * @type {ReadonlyArray<string>}
 */
const UNDISCOUNTED_ORDER_LINE_TYPES = Object.freeze([
  ORDER_LINE_TYPE.SHIPPING,
  ORDER_LINE_TYPE.GIFT_CARD,
]);

module.exports = { ORDER_LINE_TYPE, ORDER_LINE_TYPE_VALUES, UNDISCOUNTED_ORDER_LINE_TYPES };
